/**
 * App.js — RFID Attendance Dashboard
 *
 * Tabs:
 *   • Dashboard   — live attendance log + stats
 *   • Registry    — registered card management
 *
 * Data flow:
 *   ESP8266 → MQTT (HiveMQ) → useMQTT hook → React state → UI
 *   UID ↔ Person mapping stored in localStorage via useRegistry
 */

import React, { useState, useCallback, useEffect, useRef } from "react";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";

import { useMQTT }       from "./hooks/useMQTT";
import { useRegistry }   from "./hooks/useRegistry";
import Header            from "./components/Header";
import StatsCard         from "./components/StatsCard";
import LiveFeed          from "./components/LiveFeed";
import AttendanceTable   from "./components/AttendanceTable";
import RegistryTable     from "./components/RegistryTable";
import MqttInfo          from "./components/MqttInfo";
import RegisterModal     from "./components/RegisterModal";

export default function App() {
  const { records, connected, clearRecords } = useMQTT();
  const { registry, register, unregister, lookup } = useRegistry();

  // ── Auto-register: open modal when unknown card is scanned ──
  const lastAutoUID = useRef(null); // prevent re-triggering same card
  useEffect(() => {
    if (records.length === 0) return;
    const latest = records[0];
    // Only trigger for brand-new UIDs not yet in registry
    if (!lookup(latest.uid) && latest.uid !== lastAutoUID.current) {
      lastAutoUID.current = latest.uid;
      setModal({ open: true, uid: latest.uid, editInfo: null });
    }
  }, [records, lookup]);

  // ── Tab state ─────────────────────────────────────────────
  const [tab, setTab] = useState("dashboard"); // "dashboard" | "registry"

  // ── Modal state ───────────────────────────────────────────
  // { open: bool, uid: string|null, editInfo: obj|null }
  const [modal, setModal] = useState({ open: false, uid: null, editInfo: null });

  // Open to register a specific scanned UID
  const openRegisterForUID = useCallback((uid) => {
    setModal({ open: true, uid, editInfo: null });
  }, []);

  // Open blank form (from header button or registry tab)
  const openRegisterBlank = useCallback(() => {
    setModal({ open: true, uid: null, editInfo: null });
  }, []);

  // Open pre-filled for editing an existing entry
  const openEdit = useCallback((uid, info) => {
    setModal({ open: true, uid, editInfo: info });
  }, []);

  const closeModal = useCallback(() => {
    setModal({ open: false, uid: null, editInfo: null });
  }, []);

  // Save handler (new + edit)
  const handleSave = useCallback((uid, name, studentId) => {
    register(uid, name, studentId);
    toast.success(`${name} registered successfully!`, { icon: "🪪" });
    closeModal();
  }, [register, closeModal]);

  // Delete with confirm
  const handleDelete = useCallback((uid) => {
    const person = lookup(uid);
    if (window.confirm(`Remove "${person?.name || uid}" from registry?`)) {
      unregister(uid);
      toast("Card removed from registry.", { icon: "🗑️" });
    }
  }, [lookup, unregister]);

  const TABS = [
    { id: "dashboard", label: "Dashboard",  icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.2">
        <rect x="3" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="3" y="14" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/>
      </svg>
    )},
    { id: "registry",  label: "Registry",   icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    )},
  ];

  return (
    <div className="hero-bg min-h-screen">
      {/* Toast */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            fontFamily: "Inter, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
            border: "1px solid #e2e8f0",
            background: "#fff",
            color: "#1e293b",
            padding: "10px 16px",
          },
        }}
      />

      {/* Header */}
      <Header
        connected={connected}
        recordCount={records.length}
        onRegister={openRegisterBlank}
      />

      {/* Page */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Hero banner */}
        <div className="glass-card px-6 py-5 flex flex-col sm:flex-row
                        sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">
              Live Attendance Tracker
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Scan RFID cards on the ESP8266 — updates appear instantly below.
              Register cards to show names.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1.5 rounded-full bg-slate-100 text-xs font-medium text-slate-500">
              broker.hivemq.com
            </span>
            <span className="px-3 py-1.5 rounded-full bg-slate-100 text-xs font-medium text-slate-500">
              attendance/rfid
            </span>
            <button onClick={openRegisterBlank} className="btn-primary text-xs">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Register Card
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-white rounded-2xl border border-slate-200
                        shadow-sm w-fit">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm
                          font-semibold transition-all ${
                            tab === t.id
                              ? "bg-brand-600 text-white shadow-sm"
                              : "text-slate-500 hover:bg-slate-50"
                          }`}
            >
              {t.icon}
              {t.label}
              {t.id === "registry" && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold
                                  ${tab === "registry"
                                    ? "bg-white/20 text-white"
                                    : "bg-slate-100 text-slate-500"}`}>
                  {Object.keys(registry).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Dashboard tab ──────────────────────────────── */}
        {tab === "dashboard" && (
          <>
            <StatsCard records={records} />

            <section>
              <p className="text-xs font-semibold uppercase tracking-widest
                            text-slate-400 mb-2 px-1">Latest Scan</p>
              <LiveFeed latest={records[0] || null} lookup={lookup} />
            </section>

            <section>
              <p className="text-xs font-semibold uppercase tracking-widest
                            text-slate-400 mb-2 px-1">Attendance Log</p>
              <AttendanceTable
                records={records}
                onClear={clearRecords}
                connected={connected}
                lookup={lookup}
                onRegister={openRegisterForUID}
              />
            </section>

            <MqttInfo />
          </>
        )}

        {/* ── Registry tab ───────────────────────────────── */}
        {tab === "registry" && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                Card Registry
              </p>
              <button onClick={openRegisterBlank} className="btn-primary text-xs">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Register New Card
              </button>
            </div>
            <RegistryTable
              registry={registry}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          </section>
        )}

        {/* Footer */}
        <footer className="text-center py-4 text-xs text-slate-400 font-medium">
          RFID Attendance System · ESP8266 + MQTT + React ·{" "}
          <span className="text-brand-400">HiveMQ Public Broker</span>
        </footer>
      </main>

      {/* Register / Edit modal */}
      {modal.open && (
        <RegisterModal
          uid={modal.uid}
          editInfo={modal.editInfo}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
